import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Icon, type IconName } from './icons';

/**
 * نمط المعالج الموحّد: خطوات مرقّمة بشريط تقدم، وخطوة واحدة ظاهرة، ورجوع/تالٍ/تخطٍّ،
 * ومسودة محفوظة فى المتصفح. معالج الفاتورة الإلكترونية وإعداد الشركة وإضافة الصنف كلها
 * عليه. المعالج لا يحفظ شيئًا على الخادم بنفسه — كل خطوة تحفظ بمسارها المعتاد.
 */
export interface WizardStep {
  id: string;
  title: string;
  /** سطر يشرح لماذا الخطوة مهمة — لصاحب شركة غير محاسب. */
  hint?: string;
  icon?: IconName;
  /** خطوة يمكن تجاوزها والعودة إليها لاحقًا. */
  optional?: boolean;
}

/** نسبة الإنجاز المعروضة: الخطوات المكتملة من الكل، مقرّبة لأقرب عدد صحيح. */
export function progressPercent(completed: number, total: number): number {
  if (total <= 0) return 0;
  return Math.round((Math.min(Math.max(completed, 0), total) / total) * 100);
}

/**
 * أبعد خطوة يُسمح بالقفز إليها: كل ما قبلها مكتمل أو اختيارى، والخطوة الأولى غير المكتملة
 * الإجبارية حدٌّ — لا يُقفز فوقها إلى ما بعدها.
 */
export function furthestReachable(
  steps: readonly WizardStep[],
  completed: ReadonlySet<string>,
): number {
  for (let index = 0; index < steps.length; index += 1) {
    const step = steps[index]!;
    if (!completed.has(step.id) && !step.optional) return index;
  }
  return steps.length - 1;
}

/** شريط الخطوات والتقدم وحده — لمعالج يدير جسمه وأزراره بنفسه (الفاتورة الإلكترونية). */
export function WizardProgress({
  steps,
  current,
  completed,
  onStep,
  label,
  freeNavigation = false,
}: {
  steps: readonly WizardStep[];
  current: number;
  completed: ReadonlySet<string>;
  onStep: (index: number) => void;
  label: string;
  /** كل الخطوات مفتوحة للقفز (معالج خطواته عرض لحالة على الخادم لا إدخال متتابع). */
  freeNavigation?: boolean;
}) {
  const reachable = freeNavigation ? steps.length - 1 : furthestReachable(steps, completed);
  const percent = progressPercent(
    steps.filter((step) => completed.has(step.id)).length,
    steps.length,
  );
  return (
    <div className="wizard-progress">
      <div
        className="wizard-bar"
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
        aria-valuetext={percent + '٪ مكتمل'}
      >
        <span style={{ width: percent + '%' }} />
      </div>
      <ol className="wizard-steps">
        {steps.map((step, index) => {
          const done = completed.has(step.id);
          const state = index === current ? 'active' : done ? 'done' : '';
          return (
            <li key={step.id} className={state}>
              <button
                type="button"
                onClick={() => onStep(index)}
                disabled={index > reachable && index !== current}
                aria-current={index === current ? 'step' : undefined}
              >
                <span className="wizard-dot" aria-hidden="true">
                  {done ? <Icon name="check" size={14} /> : index + 1}
                </span>
                <span className="wizard-step-title">{step.title}</span>
                {step.optional && <small className="wizard-optional">اختيارى</small>}
              </button>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

interface Props {
  title: string;
  /** سطر تحت العنوان. */
  intro?: ReactNode;
  steps: readonly WizardStep[];
  current: number;
  onStep: (index: number) => void;
  completed: ReadonlySet<string>;
  /** هل يُسمح بالتالى من هذه الخطوة (البيانات المطلوبة مكتملة). */
  canNext?: boolean;
  /** يُستدعى بالتالى قبل الانتقال؛ يرجع false ليبقى (فشل الحفظ مثلاً). */
  onNext?: () => Promise<boolean> | boolean;
  onFinish?: () => void;
  finishLabel?: string;
  nextLabel?: string;
  busy?: boolean;
  /** وقت حفظ المسودة الأخير — يظهر «حُفظت المسودة» ليطمئن المستخدم أن الخروج آمن. */
  savedAt?: Date | null;
  children: ReactNode;
}

export default function Wizard({
  title,
  intro,
  steps,
  current,
  onStep,
  completed,
  canNext = true,
  onNext,
  onFinish,
  finishLabel = 'إنهاء',
  nextLabel = 'التالى',
  busy = false,
  savedAt = null,
  children,
}: Props) {
  const step = steps[current];
  const bodyRef = useRef<HTMLDivElement>(null);
  const [direction, setDirection] = useState<'forward' | 'back'>('forward');
  const last = current === steps.length - 1;

  const go = useCallback(
    (index: number) => {
      setDirection(index < current ? 'back' : 'forward');
      onStep(index);
    },
    [current, onStep],
  );

  // الخطوة الجديدة تبدأ من أعلاها، والتركيز على عنوانها لقارئ الشاشة ولوحة المفاتيح
  useEffect(() => {
    const body = bodyRef.current;
    if (!body) return;
    body.querySelector<HTMLElement>('.wizard-step-heading')?.focus({ preventScroll: true });
    const top = body.closest('.wizard')?.getBoundingClientRect().top ?? 0;
    if (top < 0) body.closest('.wizard')?.scrollIntoView({ block: 'start' });
  }, [current]);

  async function next(): Promise<void> {
    if (onNext && !(await onNext())) return;
    if (last) onFinish?.();
    else go(current + 1);
  }

  if (!step) return null;

  return (
    <section className="wizard panel" aria-label={title}>
      <header className="wizard-head">
        <div>
          <h2>{title}</h2>
          {intro && <p className="sub">{intro}</p>}
        </div>
        {savedAt && (
          <span className="wizard-saved" role="status">
            <Icon name="check" size={14} /> حُفظت المسودة
          </span>
        )}
      </header>
      <WizardProgress
        steps={steps}
        current={current}
        completed={completed}
        onStep={go}
        label={title}
      />
      <div className={'wizard-body to-' + direction} key={step.id} ref={bodyRef}>
        <div className="wizard-step-intro">
          {step.icon && (
            <span className="wizard-step-icon" aria-hidden="true">
              <Icon name={step.icon} size={22} />
            </span>
          )}
          <div>
            <p className="wizard-count">
              الخطوة {current + 1} من {steps.length}
            </p>
            <h3 className="wizard-step-heading" tabIndex={-1}>
              {step.title}
            </h3>
            {step.hint && <p className="sub">{step.hint}</p>}
          </div>
        </div>
        {children}
      </div>
      <footer className="wizard-actions">
        <button
          type="button"
          className="ghost"
          onClick={() => go(current - 1)}
          disabled={current === 0 || busy}
        >
          رجوع
        </button>
        <span className="wizard-spacer" />
        {step.optional && !last && (
          <button type="button" className="link" onClick={() => go(current + 1)} disabled={busy}>
            تخطَّ الآن
          </button>
        )}
        <button
          type="button"
          className="primary"
          onClick={() => void next()}
          disabled={!canNext || busy}
        >
          {busy ? 'جارى الحفظ…' : last ? finishLabel : nextLabel}
        </button>
      </footer>
    </section>
  );
}

/**
 * مسودة المعالج فى المتصفح: تعيش بين الزيارات حتى يُنهى أو يُلغى. لا تُحفظ فيها أسرار
 * (كلمات سر أو Client Secret) — من يستدعيها يمرّر الحقول الآمنة فقط.
 */
export function useWizardDraft<T>(
  key: string,
  initial: T,
): [T, (update: (current: T) => T) => void, () => void, Date | null] {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = window.localStorage.getItem(key);
      return raw ? { ...initial, ...(JSON.parse(raw) as Partial<T>) } : initial;
    } catch {
      return initial;
    }
  });
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  // القيمة الابتدائية كما كانت أول مرة — المسح يعيدها ولو تغيّر مرجعها بين الرسمات
  const initialRef = useRef(initial);

  const update = useCallback(
    (change: (current: T) => T) => {
      setValue((current) => {
        const next = change(current);
        try {
          window.localStorage.setItem(key, JSON.stringify(next));
          setSavedAt(new Date());
        } catch {
          // تخزين محجوب: المعالج يعمل لهذه الجلسة فقط
        }
        return next;
      });
    },
    [key],
  );

  const clear = useCallback(() => {
    try {
      window.localStorage.removeItem(key);
    } catch {
      // لا شىء يُمسح
    }
    setValue(initialRef.current);
    setSavedAt(null);
  }, [key]);

  return [value, update, clear, savedAt];
}
